import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, Key, PencilSimple, Trash, Users, Warning, Eye, EyeSlash, Copy, EnvelopeSimple, ChartBar, CaretDown, CaretRight, TrendUp, Printer, LockSimple, ShareNetwork } from '@phosphor-icons/react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import { itApi } from './itApi';
import { openPrintWindow } from '@/lib/printWindow';
import { DataTable } from '@/components/ui/DataTable';
import LicenseFormDialog from './LicenseFormDialog';
import LicenseSeatsDialog from './LicenseSeatsDialog';
import { formatDate } from '@/utils/datetime';

const yen = (n) => (typeof n === 'number' ? `¥${n.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : '—');
const fmtDate = (s) => (s ? formatDate(s) : '—');

// 過去12ヶ月の月別コスト（サブスク=月額換算 / 買い切り=購入月一括）
export function computeMonthly(items) {
  const now = new Date();
  const months = [...Array(12)].map((_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${String(d.getFullYear()).slice(2)}/${d.getMonth() + 1}`, sub: 0, per: 0 };
  });
  let any = false;
  for (const l of items) {
    if (typeof l.cost !== 'number' || l.cost <= 0) continue;
    const start = (l.purchase_date || l.created_at || '').slice(0, 7);
    if (!start) continue;
    any = true;
    if (l.license_type === 'subscription') {
      const monthly = l.cost / 12;
      const expiry = (l.expiry_date || '').slice(0, 7);
      for (const m of months) {
        if (start <= m.key && (!expiry || expiry >= m.key)) m.sub += monthly;
      }
    } else {
      const m = months.find((x) => x.key === start);
      if (m) m.per += l.cost;
    }
  }
  return any ? months.map((m) => ({ ...m, sub: Math.round(m.sub), per: Math.round(m.per) })) : [];
}

// ベンダー別集計
export function computeVendors(items) {
  const map = {};
  for (const l of items) {
    if (typeof l.cost !== 'number' || l.cost <= 0) continue;
    const v = (l.vendor || '').trim() || '（ベンダー未設定）';
    if (!map[v]) map[v] = { vendor: v, sub: 0, per: 0, count: 0 };
    map[v].count += 1;
    if (l.license_type === 'subscription') map[v].sub += l.cost;
    else map[v].per += l.cost;
  }
  return Object.values(map).sort((a, b) => (b.sub + b.per) - (a.sub + a.per));
}

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// 経営会議用 印刷レポートHTML
function buildCostReportHtml(items) {
  const vendors = computeVendors(items);
  const monthly = computeMonthly(items);
  const totalSub = vendors.reduce((a, r) => a + r.sub, 0);
  const totalPer = vendors.reduce((a, r) => a + r.per, 0);
  const maxV = Math.max(1, ...vendors.map((r) => r.sub + r.per));
  const maxM = Math.max(1, ...monthly.map((m) => m.sub + m.per));
  const expiring = items.filter((l) => l.expiry_date).sort((a, b) => String(a.expiry_date).localeCompare(String(b.expiry_date)));
  const today = formatDate(new Date());
  const bar = (w, color) => `<span style="display:inline-block;height:8px;border-radius:4px;background:${color};width:${Math.max(2, Math.round(w * 100))}%"></span>`;
  const vendorRows = vendors.map((r) => `
    <tr><td>${esc(r.vendor)}</td><td class="c">${r.count}</td>
    <td class="r">${r.sub ? yen(Math.round(r.sub)) : '—'}</td><td class="r">${r.per ? yen(Math.round(r.per)) : '—'}</td>
    <td class="r"><b>${yen(Math.round(r.sub + r.per))}</b></td>
    <td style="width:26%"><span style="display:flex;gap:1px">${r.sub ? bar(r.sub / maxV, '#6366f1') : ''}${r.per ? bar(r.per / maxV, '#a1a1aa') : ''}</span></td></tr>`).join('');
  const monthRows = monthly.map((m) => `
    <tr><td>20${esc(m.label.replace('/', '年'))}月</td>
    <td class="r">${m.sub ? yen(m.sub) : '—'}</td><td class="r">${m.per ? yen(m.per) : '—'}</td>
    <td class="r"><b>${yen(m.sub + m.per)}</b></td>
    <td style="width:30%"><span style="display:flex;gap:1px">${m.sub ? bar(m.sub / maxM, '#6366f1') : ''}${m.per ? bar(m.per / maxM, '#a1a1aa') : ''}</span></td></tr>`).join('');
  const licRows = items.map((l) => `
    <tr><td>${esc(l.name)}</td><td>${esc(l.vendor || '—')}</td><td class="c">${esc(l.type_label || '')}</td>
    <td class="c">${l.seats_used}${l.seats_total ? ` / ${l.seats_total}` : ''}</td>
    <td class="c">${l.purchase_date ? esc(String(l.purchase_date).slice(0, 10)) : '—'}</td>
    <td class="c">${l.expiry_date ? esc(String(l.expiry_date).slice(0, 10)) : '—'}${l.expired ? ' ⚠期限切れ' : (l.expiring_soon ? ' ⚠30日以内' : '')}</td>
    <td class="r">${yen(l.cost)}</td></tr>`).join('');
  const renewalRows = expiring.map((l) => `
    <tr><td>${esc(l.name)}</td><td>${esc(l.vendor || '—')}</td>
    <td class="c">${esc(String(l.expiry_date).slice(0, 10))}</td>
    <td class="c">${l.expired ? '期限切れ' : (typeof l.days_to_expiry === 'number' ? `あと${l.days_to_expiry}日` : '—')}</td>
    <td class="r">${yen(l.cost)}</td></tr>`).join('');
  return `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"><title>ライセンスコストレポート ${today}</title>
<style>
  @page{size:A4;margin:16mm 14mm}
  body{font-family:'Hiragino Kaku Gothic ProN','Yu Gothic',Meiryo,sans-serif;color:#18181b;font-size:11px;margin:0;padding:24px}
  h1{font-size:20px;margin:0}
  h2{font-size:13px;margin:22px 0 6px;padding-bottom:4px;border-bottom:2px solid #0052FF}
  .head{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #0052FF;padding-bottom:10px}
  .brand{font-size:11px;color:#0052FF;font-weight:bold;letter-spacing:.1em}
  .meta{font-size:10px;color:#71717a;text-align:right}
  .cards{display:flex;gap:8px;margin-top:14px}
  .card{flex:1;border:1px solid #e4e4e7;border-radius:8px;padding:8px 10px}
  .card p{margin:0;font-size:9px;color:#71717a}
  .card b{font-size:15px}
  table{width:100%;border-collapse:collapse;margin-top:4px}
  th{background:#f4f4f5;text-align:left;font-size:9px;color:#52525b;padding:4px 6px;border:1px solid #e4e4e7}
  td{padding:4px 6px;border:1px solid #e4e4e7;font-size:10px}
  td.r{text-align:right;white-space:nowrap} td.c{text-align:center;white-space:nowrap}
  .legend{font-size:9px;color:#71717a;margin-top:3px}
  .dot{display:inline-block;width:8px;height:8px;border-radius:2px;margin:0 3px 0 8px;vertical-align:middle}
  .note{font-size:9px;color:#a1a1aa;margin-top:16px;border-top:1px solid #e4e4e7;padding-top:6px}
  tr{page-break-inside:avoid}
</style></head><body>
<div class="head">
  <div><div class="brand">SENSBEY／ITシステム管理</div><h1>ライセンスコストレポート</h1></div>
  <div class="meta">作成日: ${today}<br>対象: ${items.length}ライセンス</div>
</div>
<div class="cards">
  <div class="card"><p>ライセンス数</p><b>${items.length}</b></div>
  <div class="card"><p>サブスク合計（年間）</p><b style="color:#4f46e5">${yen(Math.round(totalSub))}</b></div>
  <div class="card"><p>買い切り合計（一括）</p><b>${yen(Math.round(totalPer))}</b></div>
  <div class="card"><p>更新間近（30日）</p><b style="color:#d97706">${items.filter((l) => l.expiring_soon).length}</b></div>
  <div class="card"><p>期限切れ</p><b style="color:#e11d48">${items.filter((l) => l.expired).length}</b></div>
</div>
<h2>1. ベンダー別コスト</h2>
<table><tr><th>ベンダー</th><th>件数</th><th>サブスク（年間）</th><th>買い切り</th><th>合計</th><th>構成</th></tr>${vendorRows || '<tr><td colspan="6" class="c">金額入力のあるライセンスがありません</td></tr>'}</table>
<div class="legend"><span class="dot" style="background:#6366f1"></span>サブスク <span class="dot" style="background:#a1a1aa"></span>買い切り</div>
<h2>2. 月別コスト推移（過去12ヶ月）</h2>
<table><tr><th>月</th><th>サブスク（月額換算）</th><th>買い切り（購入月）</th><th>合計</th><th>推移</th></tr>${monthRows || '<tr><td colspan="5" class="c">対象データがありません</td></tr>'}</table>
<h2>3. 更新期限一覧</h2>
<table><tr><th>ソフト名</th><th>ベンダー</th><th>更新期限</th><th>残日数</th><th>金額</th></tr>${renewalRows || '<tr><td colspan="5" class="c">更新期限が設定されたライセンスはありません</td></tr>'}</table>
<h2>4. ライセンス明細</h2>
<table><tr><th>ソフト名</th><th>ベンダー</th><th>種別</th><th>座席</th><th>購入日</th><th>更新期限</th><th>金額</th></tr>${licRows}</table>
<p class="note">※ サブスクは登録金額を年間費用とみなし、月別推移では÷12の月額換算で購入日（未入力時は登録日）から更新期限まで計上。買い切りは購入月に一括計上。金額未入力のライセンスはコスト集計に含まれません。<br>Sensbey Web App（統合業務プラットフォーム）— ITシステム管理 / ライセンス管理より自動生成</p>
</body></html>`;
}

const StatCard = ({ label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4">
    <p className="text-xs text-zinc-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent || 'text-zinc-900'}`}>{value}</p>
    {sub && <p className="text-[11px] text-zinc-400 mt-0.5">{sub}</p>}
  </div>
);

// 月別コスト推移（過去12ヶ月）: サブスク=月額換算の継続費用、買い切り=購入月に一括計上
function MonthlyCostTrend({ items }) {
  const [open, setOpen] = useState(true);
  const data = useMemo(() => computeMonthly(items), [items]);
  if (data.length === 0 || data.every((m) => m.sub === 0 && m.per === 0)) return null;
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm" data-testid="license-cost-trend">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="license-cost-trend-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        {open ? <CaretDown size={14} weight="bold" className="text-zinc-400" /> : <CaretRight size={14} weight="bold" className="text-zinc-400" />}
        <TrendUp size={17} weight="duotone" className="text-emerald-600" />
        <span className="text-sm font-bold text-zinc-800">月別コスト推移（過去12ヶ月）</span>
        <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold" data-testid="cost-trend-current">
          今月 {yen(data[data.length - 1].sub + data[data.length - 1].per)}
        </span>
      </button>
      {open && (
        <div className="px-2 pb-3">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f4f4f5" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#a1a1aa' }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v >= 10000 ? `${Math.round(v / 10000)}万` : v)} width={40} />
              <Tooltip formatter={(v, name) => [yen(v), name]} labelFormatter={(l) => `20${l.replace('/', '年')}月`}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e4e4e7' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="sub" name="サブスク（月額換算）" stackId="a" fill="#6366f1" />
              <Bar dataKey="per" name="買い切り（購入月）" stackId="a" fill="#a1a1aa" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-zinc-400 px-2">サブスクは年間金額÷12の月額換算で、購入日（未入力時は登録日）から更新期限まで計上。買い切りは購入月に一括計上しています。</p>
        </div>
      )}
    </div>
  );
}

// ベンダー別コスト集計（サブスク=継続費用 / 買い切り=一括費用に分けて表示）
function VendorCostSummary({ items }) {
  const [open, setOpen] = useState(true);
  const rows = useMemo(() => computeVendors(items), [items]);
  if (rows.length === 0) return null;
  const totalSub = rows.reduce((a, r) => a + r.sub, 0);
  const totalPer = rows.reduce((a, r) => a + r.per, 0);
  const max = Math.max(...rows.map((r) => r.sub + r.per));
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm" data-testid="license-cost-summary">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="license-cost-summary-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        {open ? <CaretDown size={14} weight="bold" className="text-zinc-400" /> : <CaretRight size={14} weight="bold" className="text-zinc-400" />}
        <ChartBar size={17} weight="duotone" className="text-indigo-600" />
        <span className="text-sm font-bold text-zinc-800">ベンダー別コスト</span>
        <span className="ml-auto flex items-center gap-2 flex-wrap justify-end">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold" data-testid="cost-total-subscription">サブスク（年間）{yen(Math.round(totalSub))}</span>
          {totalPer > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200 font-semibold" data-testid="cost-total-perpetual">買い切り {yen(Math.round(totalPer))}</span>}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {rows.map((r) => {
            const total = r.sub + r.per;
            return (
              <div key={r.vendor} data-testid={`cost-vendor-${r.vendor}`}>
                <div className="flex items-center justify-between gap-2 text-xs mb-0.5">
                  <span className="font-medium text-zinc-700 truncate">{r.vendor} <span className="text-zinc-400 font-normal">×{r.count}</span></span>
                  <span className="text-zinc-600 whitespace-nowrap">
                    {r.sub > 0 && <span className="text-indigo-600 font-semibold">{yen(Math.round(r.sub))}/年</span>}
                    {r.sub > 0 && r.per > 0 && <span className="text-zinc-300 mx-1">＋</span>}
                    {r.per > 0 && <span className="text-zinc-500">{yen(Math.round(r.per))} 一括</span>}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-zinc-100 overflow-hidden flex">
                  <div className="h-full bg-indigo-500" style={{ width: `${(r.sub / max) * 100}%` }} />
                  <div className="h-full bg-zinc-400" style={{ width: `${(r.per / max) * 100}%` }} />
                </div>
              </div>
            );
          })}
          <p className="text-[10px] text-zinc-400 pt-1">サブスクは登録金額を年間費用として集計しています。金額未入力のライセンスは含まれません。</p>
        </div>
      )}
    </div>
  );
}

export default function LicenseManagement() {
  const { user } = useAuth();
  if (!canEditIt(user)) {
    return <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center text-sm text-zinc-500" data-testid="license-no-access">このページを利用するにはIT編集の権限が必要です。</div>;
  }
  return <LicenseAdmin />;
}

function LicenseAdmin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [seatsId, setSeatsId] = useState(null);
  const [revealed, setRevealed] = useState({});

  const load = useCallback(() => {
    itApi.licenses().then(setItems).catch(() => {});
    itApi.licenseStats().then(setStats).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (l) => {
    if (!window.confirm(`「${l.name}」を削除しますか？（割当も削除されます）`)) return;
    try { await itApi.deleteLicense(l.id); toast.success('削除しました'); load(); }
    catch { toast.error('削除に失敗しました'); }
  };

  const toggleReveal = async (l) => {
    if (revealed[l.id] !== undefined) {
      setRevealed((p) => { const n = { ...p }; delete n[l.id]; return n; });
      return;
    }
    try {
      const { password } = await itApi.revealLicense(l.id);
      setRevealed((p) => ({ ...p, [l.id]: password || '' }));
    } catch { toast.error('パスワードの取得に失敗しました'); }
  };

  // コピーも監査ログに残す（誰が・いつ・どのライセンスの何を）
  const copyText = async (v, licenseId, field) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success('コピーしました');
      if (licenseId) itApi.logCredentialCopy(licenseId, field).catch(() => {});
    } catch { toast.error('コピーに失敗しました'); }
  };

  return (
    <div className="space-y-5" data-testid="license-management">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">ライセンス管理</h1>
        <div className="flex gap-2">
          <Button data-testid="license-report-btn" variant="outline" onClick={() => openPrintWindow(buildCostReportHtml(items))} className="h-11 rounded-lg" disabled={items.length === 0}>
            <Printer size={18} className="mr-1.5" /> コストレポート
          </Button>
          <Button data-testid="license-new-btn" onClick={() => { setEditItem(null); setFormOpen(true); }} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> 新規登録
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="license-stats">
          <StatCard testid="license-stat-total" label="ライセンス" value={stats.total} />
          <StatCard testid="license-stat-seats" label="座席 使用 / 総数" value={`${stats.seats_used} / ${stats.seats_total || '∞'}`} accent="text-teal-700" />
          <StatCard testid="license-stat-cost" label="費用合計" value={yen(stats.total_cost)} />
          <StatCard testid="license-stat-expiring" label="更新間近(30日)" value={stats.expiring_soon} accent={stats.expiring_soon ? 'text-amber-600' : 'text-zinc-900'} />
          <StatCard testid="license-stat-expired" label="期限切れ" value={stats.expired} accent={stats.expired ? 'text-rose-600' : 'text-zinc-900'} />
        </div>
      )}

      <VendorCostSummary items={items} />
      <MonthlyCostTrend items={items} />

      <DataTable
        dataTestId="license-table"
        rows={items}
        rowKey={(l) => l.id}
        rowTestId={(l) => `license-row-${l.id}`}
        empty={{ icon: <Key size={40} className="text-zinc-300 mx-auto mb-2" weight="duotone" />, text: 'ライセンスがありません', testid: 'license-empty' }}
        columns={[
          { key: 'name', primary: true, header: 'ソフト名', cell: (l) => <span className="font-medium text-zinc-800">{l.name}</span> },
          { key: 'vendor', header: 'ベンダー', cell: (l) => <span className="text-zinc-600" data-no-translate>{l.vendor || '—'}</span> },
          { key: 'sharing', header: '共有', cell: (l) => (
            l.is_shared ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-teal-700" data-testid={`license-share-badge-${l.id}`}>
                <ShareNetwork size={12} />
                {(l.shared_employee_ids || []).length > 0 && `${l.shared_employee_ids.length}名`}
                {(l.shared_employee_ids || []).length > 0 && (l.shared_department_ids || []).length > 0 && '・'}
                {(l.shared_department_ids || []).length > 0 && `${l.shared_department_ids.length}部署`}
              </span>
            ) : <span className="text-[11px] text-zinc-300" data-testid={`license-share-badge-${l.id}`}>管理者のみ</span>
          ) },
          { key: 'type', header: '', cell: (l) => <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{l.type_label}</Badge> },
          { key: 'seats', header: '座席', cell: (l) => (
            <div>
              <button data-testid={`license-seats-${l.id}`} onClick={() => setSeatsId(l.id)} className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-teal-700">
                <Users size={15} /> {l.seats_used}{l.seats_total ? ` / ${l.seats_total}` : ' / ∞'}
              </button>
              {(l.admin_names || []).length > 0 && (
                <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`license-admin-names-${l.id}`}>管理者: {l.admin_names.join('、')}</div>
              )}
            </div>
          ) },
          { key: 'login', header: 'ログイン情報', cell: (l) => (
            <span className="text-xs" data-testid={`license-login-${l.id}`}>
              {!l.can_view_credentials ? (
                <span className="inline-flex items-center gap-1 text-zinc-400" data-testid={`license-login-locked-${l.id}`}>
                  <LockSimple size={12} /> 共有されていません
                </span>
              ) : (l.login_email || l.has_password) ? (
                <div className="space-y-1">
                  {l.login_email && (
                    <div className="inline-flex items-center gap-1 text-zinc-600 max-w-[200px]">
                      <EnvelopeSimple size={12} className="shrink-0" />
                      <span className="font-mono truncate">{l.login_email}</span>
                      <button onClick={() => copyText(l.login_email, l.id, 'email')} data-testid={`license-login-copy-${l.id}`} className="text-zinc-400 hover:text-teal-700 shrink-0" title="コピー"><Copy size={12} /></button>
                    </div>
                  )}
                  {l.has_password && !l.can_reveal_password && (
                    <span className="inline-flex items-center gap-1 text-zinc-400" data-testid={`license-pw-locked-${l.id}`}>
                      <LockSimple size={12} /> パスワードは管理者と共有先のみ
                    </span>
                  )}
                  {l.has_password && l.can_reveal_password && (revealed[l.id] !== undefined ? (
                    <div className="inline-flex items-center gap-1.5">
                      <span className="font-mono bg-zinc-100 rounded px-2 py-0.5" data-testid={`license-pw-value-${l.id}`}>{revealed[l.id] || '（空）'}</span>
                      {revealed[l.id] && <button onClick={() => copyText(revealed[l.id], l.id, 'password')} data-testid={`license-pw-copy-${l.id}`} className="text-zinc-400 hover:text-teal-700" title="コピー"><Copy size={13} /></button>}
                      <button onClick={() => toggleReveal(l)} data-testid={`license-pw-hide-${l.id}`} className="text-zinc-400 hover:text-zinc-700" title="隠す"><EyeSlash size={13} /></button>
                    </div>
                  ) : (
                    <button onClick={() => toggleReveal(l)} data-testid={`license-pw-reveal-${l.id}`} className="inline-flex items-center gap-1 text-zinc-500 hover:text-teal-700">
                      <Eye size={13} /> パスワード表示
                    </button>
                  ))}
                </div>
              ) : <span className="text-zinc-300">—</span>}
            </span>
          ) },
          { key: 'expiry', header: '更新期限', cell: (l) => (
            <span className="whitespace-nowrap">{fmtDate(l.expiry_date)}
              {l.expired && <Badge variant="outline" className="ml-1.5 bg-rose-50 text-rose-700 border-rose-200">期限切れ</Badge>}
              {!l.expired && l.expiring_soon && <Warning size={14} weight="fill" className="inline ml-1.5 text-amber-500" />}
            </span>
          ) },
          { key: 'cost', header: '金額', align: 'right', cell: (l) => <span className="text-zinc-700 whitespace-nowrap">{yen(l.cost)}</span> },
          { key: 'actions', actions: true, header: '', cell: (l) => (<>
            <Button size="sm" variant="ghost" data-testid={`license-manage-${l.id}`} onClick={() => setSeatsId(l.id)} className="h-9 px-2 text-zinc-600" title="座席割当"><Users size={16} /></Button>
            <Button size="sm" variant="ghost" data-testid={`license-edit-${l.id}`} onClick={() => { setEditItem(l); setFormOpen(true); }} className="h-9 px-2 text-zinc-600" title="編集"><PencilSimple size={16} /></Button>
            <Button size="sm" variant="ghost" data-testid={`license-delete-${l.id}`} onClick={() => del(l)} className="h-9 px-2 text-red-600 hover:bg-red-50" title="削除"><Trash size={16} /></Button>
          </>) },
        ]}
      />

      <LicenseFormDialog open={formOpen} onClose={() => setFormOpen(false)} license={editItem} onSaved={load} />
      <LicenseSeatsDialog open={!!seatsId} onClose={() => setSeatsId(null)} licenseId={seatsId} onChanged={load} />
    </div>
  );
}
